/* eslint-disable max-classes-per-file */
import { type CfAccountInfo } from '@chainflip/rpc/types';
import { assert, unreachable } from '@chainflip/utils/assertion';
import * as base58 from '@chainflip/utils/base58';
import { bytesToHex } from '@chainflip/utils/bytes';
import {
  type ChainflipChain,
  type ChainflipAsset,
  assetConstants,
  type ChainMap,
} from '@chainflip/utils/chainflip';
import * as ss58 from '@chainflip/utils/ss58';
import { type ApiPromise } from '@polkadot/api';
import { type SubmittableExtrinsic } from '@polkadot/api/types';
import { type QueryClient } from '@tanstack/react-query';
import { Enum, Bytes } from 'scale-ts';
import { hexToBytes, type WalletClient } from 'viem';
import { z } from 'zod';
import ExtrinsicSDK from '@/shared/utils/ExtrinsicSDK';

// The key order encodes the SCALE variant index and must match the runtime's `EncodedAddress`
// enum (state-chain/chains/src/address.rs) exactly — only ever append.
const encodedAddressCodec = Enum({
  Eth: Bytes(20),
  Dot: Bytes(32),
  Btc: Bytes(),
  Arb: Bytes(20),
  Sol: Bytes(32),
  Hub: Bytes(32),
  Tron: Bytes(20),
  Bsc: Bytes(20),
});

function addressToBytes(chain: ChainflipChain, address: string) {
  switch (chain) {
    case 'Bitcoin':
      return new TextEncoder().encode(address);
    case 'Solana':
      return base58.decode(address);
    case 'Ethereum':
    case 'Arbitrum':
    case 'Bsc':
      return hexToBytes(address as `0x${string}`);
    case 'Assethub':
      return ss58.decode(address).data;
    case 'Tron':
      return base58.decode(address).slice(1, 21);
    default:
      return unreachable(chain);
  }
}

const chainToShortChain = {
  Ethereum: 'Eth',
  Bitcoin: 'Btc',
  Arbitrum: 'Arb',
  Solana: 'Sol',
  Tron: 'Tron',
  Assethub: 'Hub',
  Bsc: 'Bsc',
} as const satisfies ChainMap<string>;

const eipPayloadSchema = z.object({
  domain: z.any(),
  types: z.any(),
  message: z.any(),
  primaryType: z.string(),
});

const encodedNonNativeCallSchema = z.object({ Eip712: eipPayloadSchema }).strict();
const encodedBytesSchema = z.object({ String: z.string() }).strict();

const transactionMetadataSchema = z
  .object({ nonce: z.number(), expiry_block: z.number() })
  .transform(({ nonce, expiry_block: expiryBlock }) => ({ nonce, expiryBlock }));

const encodeNonNativeCallResponseSchema = z.tuple([
  z.union([encodedNonNativeCallSchema, encodedBytesSchema]),
  transactionMetadataSchema,
]);

type SendCallback = Parameters<SubmittableExtrinsic<'promise'>['send']>[0];

class EvmExtrinsicSDK extends ExtrinsicSDK {
  constructor(
    account: CfAccountInfo,
    apiPromise: ApiPromise,
    accountId: `cF${string}`,
    refetch: () => void,
    queryClient: QueryClient,
    private readonly walletClient: WalletClient,
  ) {
    super(account, apiPromise, accountId, refetch, queryClient);
  }

  private getAccountAddress() {
    const evmAddress = this.walletClient.account?.address;
    assert(evmAddress, 'EVM wallet client has no connected account');
    return evmAddress;
  }

  protected async signExtrinsic(extrinsic: SubmittableExtrinsic<'promise'>) {
    const callData = bytesToHex(this.apiPromise.createType('Call', extrinsic.method).toU8a());

    const rpcResponse = await this.apiPromise.rpc(
      'cf_encode_non_native_call',
      callData,
      // TODO(evm-account): validate that this is enough time in real world scenarios.
      // this value tells the RPC to set the tx expiry to N + 100 state chain blocks.
      // this is equivalent to 10 minutes given a 6 second block time. if the tx
      // is witnessed after this deadline, it is simply discarded. it should be
      // more than enough because we have the user sign the payload and then
      // submit directly submit the tx after signing. it shouldn't take 10
      // minutes to complete this process, but if we change the logic in the
      // future to hold onto the signed payload before submitting it, we have to
      // adjust this number
      100,
      this.accountId,
      { Eth: 'Eip712' },
    );

    const [eipPayload, transactionMetadata] = encodeNonNativeCallResponseSchema.parse(rpcResponse);

    assert('Eip712' in eipPayload, 'Eip712 payload expected for EVM non-native call');

    const { domain, types, message, primaryType } = eipPayload.Eip712;

    const account = this.getAccountAddress();

    const evmSignatureEip712 = await this.walletClient.signTypedData({
      domain,
      types,
      message,
      primaryType,
      account,
    });

    return {
      callData,
      transactionMetadata,
      signatureData: {
        Ethereum: {
          signature: evmSignatureEip712,
          signer: account,
          sigType: 'Eip712' as const,
        },
      },
    };
  }

  override async submit(extrinsic: SubmittableExtrinsic<'promise'>, handleResult: SendCallback) {
    const { callData, transactionMetadata, signatureData } = await this.signExtrinsic(extrinsic);

    return this.apiPromise.tx.environment
      .nonNativeSignedCall({ call: callData, transactionMetadata }, signatureData)
      .send(handleResult);
  }

  override async signAndSendBatch(callback: (sdk: ExtrinsicSDK) => void) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const batchSdk = new BatchEvmExtrinsicSDK(
      this.account,
      this.apiPromise,
      this.accountId,
      this.refetch,
      this.queryClient,
      this.walletClient,
    );

    callback(batchSdk);
    return this.signAndSend(this.apiPromise.tx.environment.batch(batchSdk.getExtrinsics()));
  }

  override async signAccountCreationPayload(
    asset: ChainflipAsset,
    refundAddress: string,
    boostFeeBps?: number,
  ) {
    const { chain } = assetConstants[asset];

    const remarkData = encodedAddressCodec.enc({
      tag: chainToShortChain[chain],
      value: addressToBytes(chain, refundAddress),
    });

    const call = this.apiPromise.tx.system.remark(Array.from(remarkData));

    const { transactionMetadata, signatureData } = await this.signExtrinsic(call);

    return {
      signatureData,
      transactionMetadata,
      asset,
      refundAddress,
      boostFeeBps,
    };
  }
}

class BatchEvmExtrinsicSDK extends EvmExtrinsicSDK {
  private readonly extrinsics: SubmittableExtrinsic<'promise'>[] = [];

  override async signAndSend(extrinsic: SubmittableExtrinsic<'promise'>) {
    this.extrinsics.push(extrinsic);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Promise.resolve(null as any);
  }

  getExtrinsics() {
    return this.extrinsics;
  }
}

export default EvmExtrinsicSDK;
